/**
 * #10765 (H3, EPIC #9970): the homepage live strip under the hero. It grew from Night Flight's
 * NextEventStrip: the next event, the latest field note and a featured client quote, each from
 * a public CMS route (./live-strip). A source with nothing shows a calm empty cell, never a gap.
 * Night Flight's theme classes (nf-*) give it both the night scene and the doodle look.
 */
import { ArrowRight, CalendarDays, NotebookPen, Quote } from 'lucide-react';
import type { SiteEvent } from '../../lib/events';
import { useFirst, type Post, type Testimonial } from './studio-kit/data';
import { LIVE_STRIP_SOURCES, eventCell, postCell, testimonialCell, type LiveCell } from './live-strip';

const ICONS = { event: CalendarDays, post: NotebookPen, testimonial: Quote } as const;

function Cell({ cell }: { cell: LiveCell }) {
  const Icon = ICONS[cell.kind];
  return (
    <a
      href={cell.href}
      data-live-cell={cell.kind}
      data-live-empty={cell.empty ? 'true' : undefined}
      className="nf-card group flex min-w-0 flex-col gap-1.5 rounded-2xl border p-4 no-underline transition-transform hover:-translate-y-0.5"
    >
      <span className="nf-accent inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide">
        <Icon className="h-4 w-4" aria-hidden />
        {cell.label}
      </span>
      <span className={`nf-ink line-clamp-2 ${cell.kind === 'testimonial' && !cell.empty ? 'italic' : 'font-semibold'}`}>
        {cell.kind === 'testimonial' && !cell.empty ? `“${cell.text}”` : cell.text}
      </span>
      {cell.meta ? <span className="nf-ink-faint text-sm">{cell.meta}</span> : null}
      <span className="nf-accent mt-auto inline-flex items-center gap-1 pt-1 text-sm font-semibold group-hover:underline">
        {cell.cta} <ArrowRight className="h-4 w-4" aria-hidden />
      </span>
    </a>
  );
}

export function LiveStrip() {
  const event = useFirst<SiteEvent>(LIVE_STRIP_SOURCES.event);
  const post = useFirst<Post>(LIVE_STRIP_SOURCES.post);
  const quote = useFirst<Testimonial>(LIVE_STRIP_SOURCES.testimonial);
  const loading = event.loading || post.loading || quote.loading;

  return (
    <div role="region" aria-label="What is new at SmallBiz Innovation" className="px-4 py-6 sm:px-6 lg:px-8">
      {/* Hold the strip's height while the reads land, so the page below does not jump (CLS). */}
      <div className="mx-auto grid max-w-7xl gap-4 md:grid-cols-3 md:min-h-[148px]" aria-busy={loading || undefined}>
        {loading ? (
          [0, 1, 2].map((i) => <div key={i} className="nf-card h-[148px] rounded-2xl border opacity-60" aria-hidden />)
        ) : (
          <>
            <Cell cell={eventCell(event.data)} />
            <Cell cell={postCell(post.data)} />
            <Cell cell={testimonialCell(quote.data)} />
          </>
        )}
      </div>
    </div>
  );
}
